import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, RefreshCw, MoreHorizontal, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useWhatsAppMessageTemplates, WhatsAppMessageTemplate } from "@/hooks/useWhatsAppMessageTemplates";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SearchBar } from "@/components/common/SearchBar";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

const categoryLabels: Record<string, string> = {
  UTILITY: "Utilidade",
  MARKETING: "Marketing",
  AUTHENTICATION: "Autenticação",
  SERVICE: "Serviço",
};

const languageLabels: Record<string, string> = {
  pt_BR: "Português",
  pt: "Português",
  en: "Inglês",
  en_US: "Inglês",
  es: "Espanhol",
  es_ES: "Espanhol",
};

const statusConfig: Record<string, { variant: "success" | "warning" | "danger" | "info" | "neutral"; label: string }> = {
  approved: { variant: "success", label: "Aprovado" },
  pending_approval: { variant: "warning", label: "Aguardando" },
  rejected: { variant: "danger", label: "Rejeitado" },
  draft: { variant: "neutral", label: "Rascunho" },
};

export default function WhatsAppMessageTemplatesSettings() {
  const navigate = useNavigate();
  const { templates, isLoading, createTemplate, updateTemplate, deleteTemplate, submitToTwilio, syncTemplates } = useWhatsAppMessageTemplates();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [languageFilter, setLanguageFilter] = useState<string>("all");

  useEffect(() => {
    const channel = supabase
      .channel('whatsapp-templates-changes')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'whatsapp_message_templates' }, () => {})
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const handleCreate = () => navigate('/settings/communication/templates/new');
  const handleEdit = (template: WhatsAppMessageTemplate) => navigate(`/settings/communication/templates/${template.id}`);
  const handleDeleteClick = (id: string) => { setTemplateToDelete(id); setDeleteDialogOpen(true); };
  const handleDeleteConfirm = () => {
    if (templateToDelete) { deleteTemplate.mutate(templateToDelete); setDeleteDialogOpen(false); setTemplateToDelete(null); }
  };

  const filteredTemplates = templates?.filter((t) => {
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.template_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.body_text?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || t.status === statusFilter;
    const matchesCategory = categoryFilter === "all" || t.category === categoryFilter;
    const matchesLanguage = languageFilter === "all" || t.language === languageFilter;
    return matchesSearch && matchesStatus && matchesCategory && matchesLanguage;
  });

  const uniqueCategories = [...new Set(templates?.map(t => t.category) || [])];
  const uniqueLanguages = [...new Set(templates?.map(t => t.language) || [])];

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">WhatsApp Message Templates</h1>
          <p className="text-muted-foreground mt-1">
            Gerencie templates de mensagem para uso após 24h de inatividade
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => syncTemplates.mutate()}
            disabled={syncTemplates.isPending}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${syncTemplates.isPending ? 'animate-spin' : ''}`} />
            Sincronizar
          </Button>
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Template
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <SearchBar
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          placeholder="Buscar templates..."
          className="w-[280px]"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="draft">Rascunho</SelectItem>
            <SelectItem value="pending_approval">Aguardando</SelectItem>
            <SelectItem value="approved">Aprovado</SelectItem>
            <SelectItem value="rejected">Rejeitado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {uniqueCategories.map(cat => (
              <SelectItem key={cat} value={cat}>{categoryLabels[cat] || cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={languageFilter} onValueChange={setLanguageFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Idioma" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os idiomas</SelectItem>
            {uniqueLanguages.map(lang => (
              <SelectItem key={lang} value={lang}>{languageLabels[lang] || lang}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Templates Table */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Carregando...</div>
      ) : filteredTemplates && filteredTemplates.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[35%]">Nome</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Idioma</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criado</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTemplates.map((template) => {
                  const sc = statusConfig[template.status] || statusConfig.draft;
                  return (
                    <TableRow key={template.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{template.name}</div>
                          <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                            {template.body_text}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge variant="info">
                          {categoryLabels[template.category] || template.category}
                        </StatusBadge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {languageLabels[template.language] || template.language}
                      </TableCell>
                      <TableCell>
                        <StatusBadge variant={sc.variant}>{sc.label}</StatusBadge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDistanceToNow(new Date(template.created_at), { addSuffix: true, locale: ptBR })}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(template)}>
                              Editar
                            </DropdownMenuItem>
                            {(template.status === 'draft' || template.status === 'rejected' ||
                              (template.status === 'pending_approval' && !template.template_id)) && (
                              <DropdownMenuItem
                                onClick={() => submitToTwilio.mutate(template.id)}
                                disabled={submitToTwilio.isPending}
                              >
                                Enviar p/ Aprovação
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleDeleteClick(template.id)}
                            >
                              Deletar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {searchQuery || statusFilter !== "all" || categoryFilter !== "all" || languageFilter !== "all"
              ? "Nenhum template encontrado com os filtros aplicados."
              : "Nenhum template cadastrado. Crie seu primeiro template!"}
          </CardContent>
        </Card>
      )}

      {/* Form modal removed - now uses dedicated page */}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja deletar este template? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Deletar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
